import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import heroBg from '../assets/img/dca-sunset-pier-hero.jpg';
import polaroidImg from '../assets/img/mainstreet-sunglasses.png';
import heroLogo from '../assets/img/mw-logo-2026.png';
import styles from './Home.module.css';
import fx from './HomeFx.module.css';

type TwinkleWordProps = {
  text: string;
  className: string;
};

const TwinkleWord = ({ text, className }: TwinkleWordProps) => {
  const letters = Array.from(text);
  return (
    <span className={`${fx.word} ${className}`}>
      {letters.map((ch, i) => (
        <span
          key={i}
          className={fx.letter}
          style={{ ['--i' as any]: i } as React.CSSProperties}
        >
          {ch}
        </span>
      ))}
    </span>
  );
};

const Home = () => {
  const currentYear = new Date().getFullYear();
  const particles = [
    // Pixie dust: a mix of logo-blue, logo-pink, and gold.
    { x: '8%', delay: '0s', dur: '16s', size: '3px', color: 'rgba(255, 204, 61, 0.92)', glow: 'rgba(255, 204, 61, 0.22)' },
    { x: '14%', delay: '2s', dur: '20s', size: '2px', color: 'rgba(148, 176, 227, 0.92)', glow: 'rgba(148, 176, 227, 0.22)' },
    { x: '19%', delay: '6s', dur: '18s', size: '3px', color: 'rgba(255, 77, 141, 0.88)', glow: 'rgba(255, 77, 141, 0.18)' },
    { x: '26%', delay: '3s', dur: '22s', size: '2px', color: 'rgba(148, 176, 227, 0.9)', glow: 'rgba(148, 176, 227, 0.2)' },
    { x: '33%', delay: '9s', dur: '17s', size: '3px', color: 'rgba(255, 204, 61, 0.9)', glow: 'rgba(255, 204, 61, 0.2)' },
    { x: '38%', delay: '5s', dur: '21s', size: '2px', color: 'rgba(255, 77, 141, 0.84)', glow: 'rgba(255, 77, 141, 0.16)' },
    { x: '44%', delay: '11s', dur: '19s', size: '3px', color: 'rgba(148, 176, 227, 0.9)', glow: 'rgba(148, 176, 227, 0.2)' },
    { x: '51%', delay: '7s', dur: '23s', size: '2px', color: 'rgba(255, 204, 61, 0.9)', glow: 'rgba(255, 204, 61, 0.2)' },
    { x: '58%', delay: '1s', dur: '18s', size: '3px', color: 'rgba(255, 77, 141, 0.86)', glow: 'rgba(255, 77, 141, 0.18)' },
    { x: '64%', delay: '8s', dur: '20s', size: '2px', color: 'rgba(148, 176, 227, 0.92)', glow: 'rgba(148, 176, 227, 0.22)' },
    { x: '71%', delay: '4s', dur: '24s', size: '3px', color: 'rgba(255, 204, 61, 0.9)', glow: 'rgba(255, 204, 61, 0.2)' },
    { x: '78%', delay: '10s', dur: '19s', size: '2px', color: 'rgba(255, 77, 141, 0.84)', glow: 'rgba(255, 77, 141, 0.16)' },
    { x: '84%', delay: '6s', dur: '22s', size: '3px', color: 'rgba(148, 176, 227, 0.9)', glow: 'rgba(148, 176, 227, 0.2)' },
    { x: '92%', delay: '12s', dur: '17s', size: '2px', color: 'rgba(255, 204, 61, 0.9)', glow: 'rgba(255, 204, 61, 0.2)' },
  ];

  return (
    <>
      <Helmet>
        <title>MouseWait | Disneyland, Disney World, and Theme Park Friends</title>
        <meta
          name="description"
          content="MouseWait helps families and theme park fans make memories, swap tips, and meet new friends in real-time lounges for Disneyland, Disney World, and beyond."
        />
      </Helmet>

      <div className={styles.page}>
        <div className={fx.bgGrid} aria-hidden="true" />
        <div className={fx.particles} aria-hidden="true">
          {particles.map((p, idx) => (
            <span
              // Deterministic list; no randomness so UI doesn't "jump" between renders.
              key={idx}
              className={fx.particle}
              style={
                {
                  ['--x' as any]: p.x,
                  ['--delay' as any]: p.delay,
                  ['--dur' as any]: p.dur,
                  ['--size' as any]: p.size,
                  ['--pcolor' as any]: p.color,
                  ['--pglow' as any]: p.glow,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        <section className={`${styles.hero} ${fx.layer}`}>
          <div className={styles.heroBg} style={{ backgroundImage: `url(${heroBg})` }} aria-hidden="true" />
          <div className={styles.heroOverlay} aria-hidden="true" />
          <div className={fx.magic} aria-hidden="true" />
          <div className={fx.fireworks} aria-hidden="true">
            <span
              className={fx.burst}
              style={{ ['--x' as any]: '16%', ['--y' as any]: '32%', ['--delay' as any]: '0.2s', ['--dur' as any]: '5.4s' } as React.CSSProperties}
            />
            <span
              className={fx.burst}
              style={{ ['--x' as any]: '72%', ['--y' as any]: '24%', ['--delay' as any]: '1.6s', ['--dur' as any]: '6.2s' } as React.CSSProperties}
            />
            <span
              className={fx.burst}
              style={{ ['--x' as any]: '86%', ['--y' as any]: '46%', ['--delay' as any]: '3.2s', ['--dur' as any]: '6.8s' } as React.CSSProperties}
            />
            <span
              className={fx.burst}
              style={{ ['--x' as any]: '42%', ['--y' as any]: '18%', ['--delay' as any]: '2.6s', ['--dur' as any]: '7.4s' } as React.CSSProperties}
            />
            <span
              className={fx.burst}
              style={{ ['--x' as any]: '58%', ['--y' as any]: '52%', ['--delay' as any]: '4.1s', ['--dur' as any]: '6.0s' } as React.CSSProperties}
            />
            <span
              className={fx.burst}
              style={{ ['--x' as any]: '30%', ['--y' as any]: '56%', ['--delay' as any]: '5.2s', ['--dur' as any]: '7.0s' } as React.CSSProperties}
            />
          </div>
          <div className={styles.heroInner}>
            <div>
              <div className={`${styles.heroLogoWrap} ${fx.reveal1}`} aria-hidden="true">
                <img className={styles.heroLogo} src={heroLogo} alt="" />
              </div>
              <h1 className={`${styles.heroTitle} ${fx.reveal1}`}>
                Make <TwinkleWord text="memories" className={fx.wordBlue} />. Meet{' '}
                <TwinkleWord text="friends" className={fx.wordPink} />. Love the parks.
              </h1>
              <p className={`${styles.heroLead} ${fx.reveal2}`}>
                MouseWait is built for families and theme park fans who want the trip to feel like a story: shared tips,
                spontaneous meetups, and a friendly place to hang out while you wait.
              </p>
              <div className={`${styles.ctaRow} ${fx.reveal3}`}>
                <a className={`${styles.btn} ${styles.btnPrimary}`} href="/disneyland-dining-alerts/">
                  Disneyland Dining Alerts
                </a>
                <Link className={`${styles.btn} ${styles.btnPrimary}`} to="/disneyland/lounge">
                  Enter Disneyland Lounge
                </Link>
                <Link className={`${styles.btn} ${styles.btnSecondary}`} to="/disneyworld/lounge">
                  Enter Disney World Lounge
                </Link>
                <a
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  href="https://mousewait.net/#email"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get the weekly newsletter
                </a>
              </div>
            </div>

            <aside className={styles.heroAside} aria-label="Highlights">
              <div className={styles.polaroid}>
                <img className={styles.polaroidImg} src={polaroidImg} alt="A sunny Main Street moment" />
                <div className={styles.polaroidCaption}>Today’s vibe: keep it light, keep it magical.</div>
              </div>
              <div className={styles.ticket}>
                <div className={styles.ticketTitle}>Your pocket guide for park days</div>
                <div className={styles.ticketMeta}>
                  Live lounges, trip chatter, and that “we were there” feeling. Bring the family. Leave with friends.
                </div>
              </div>
            </aside>
          </div>

          <div className={fx.heroScroll} aria-hidden="true">
            <span>Scroll</span>
            <span className={fx.scrollDot} />
          </div>
        </section>

        <main className={`${styles.content} ${fx.layer}`}>
          <h2 className={styles.sectionTitle}>Built for family trips and theme park friendships</h2>
          <p className={styles.sectionLead}>
            Modern tools, nostalgic energy. Think: postcard memories, ticket-stub moments, and a community that feels
            like it has always been there.
          </p>

          <section className={styles.diningFeature} aria-labelledby="dining-alerts-title">
            <div className={styles.diningCopy}>
              <p className={styles.kicker}>Disneyland dining alerts</p>
              <h2 id="dining-alerts-title" className={styles.diningTitle}>
                Catch Disneyland dining openings before they disappear
              </h2>
              <p className={styles.diningBody}>
                MouseWait helps Disneyland visitors watch for dining reservation openings and get alerts when a matching
                restaurant, date, time, or party size appears. Use it for hard-to-get Disneyland dining times while you
                plan the rest of your park day with wait times, Crowd Index, food tips, and the MouseWait community.
              </p>
              <div className={styles.diningActions}>
                <a className={`${styles.btn} ${styles.btnPrimary}`} href="/disneyland-dining-alerts/">
                  Learn about Disneyland dining alerts
                </a>
                <a
                  className={`${styles.btn} ${styles.btnOutlineDark}`}
                  href="https://apps.apple.com/us/app/mousewait-for-disneyland/id331771613"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get the Disneyland app
                </a>
              </div>
            </div>
            <div className={styles.diningStats} aria-label="Dining alert features">
              <div>
                <strong>Reservation alerts</strong>
                <span>Watch for Disneyland dining availability.</span>
              </div>
              <div>
                <strong>Email and push</strong>
                <span>Move quickly when an opening appears.</span>
              </div>
              <div>
                <strong>Park-day context</strong>
                <span>Plan dining alongside live MouseWait tools.</span>
              </div>
            </div>
          </section>

          <div className={styles.grid3}>
            <div className={styles.card}>
              <div className={styles.cardInner}>
                <h3 className={styles.cardTitle}>Real-time lounges</h3>
                <p className={styles.cardBody}>
                  Swap tips, ask questions, share quick updates, and find people who are in the same park headspace.
                </p>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardInner}>
                <h3 className={styles.cardTitle}>Meetups, minus the awkward</h3>
                <p className={styles.cardBody}>
                  Make it easy for families and friends to connect: casual hellos, shared snacks, and “see you by the
                  landmark” moments.
                </p>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardInner}>
                <h3 className={styles.cardTitle}>Memory-friendly by design</h3>
                <p className={styles.cardBody}>
                  The best trips have texture. MouseWait is made to capture the little details you’ll talk about for
                  years.
                </p>
              </div>
            </div>
          </div>

          <div className={styles.steps} aria-label="How it works">
            <div className={styles.step}>
              <div className={styles.stepNum} aria-hidden="true">
                1
              </div>
              <h3 className={styles.stepTitle}>Pick your park</h3>
              <p className={styles.stepBody}>Jump into Disneyland or Disney World lounges and see what people are saying now.</p>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNum} aria-hidden="true">
                2
              </div>
              <h3 className={styles.stepTitle}>Join the conversation</h3>
              <p className={styles.stepBody}>Ask, share, and connect. Keep it friendly. Keep it fun. Make it yours.</p>
            </div>
          </div>

          <div className={styles.ctaBand}>
            <div className={styles.ctaBandText}>
              <p className={styles.ctaBandTitle}>Install the iOS apps</p>
              <p className={styles.ctaBandBody}>
                Disneyland and Disney World versions in the App Store. Same community energy, built for park days.
              </p>
            </div>
            <div className={styles.ctaRow}>
              <a
                className={`${styles.btn} ${styles.btnPrimary}`}
                href="https://apps.apple.com/us/app/mousewait-for-disneyland/id331771613"
                target="_blank"
                rel="noopener noreferrer"
              >
                iOS: Disneyland
              </a>
              <a
                className={`${styles.btn} ${styles.btnOutlineDark}`}
                href="https://apps.apple.com/us/app/mousewait-for-disney-world/id437292247"
                target="_blank"
                rel="noopener noreferrer"
              >
                iOS: Disney World
              </a>
            </div>
          </div>
        </main>

        <footer className={`${styles.footer} ${fx.layer}`}>
          <div className={styles.footerInner}>
            <div className={styles.footerLinks} aria-label="Footer links">
              <Link to="/disneyland/lounge">Disneyland Real-Time Lounge</Link>
              <Link to="/disneyworld/lounge">Disney World Lounge</Link>
              <a href="https://mousewait.net/#email" target="_blank" rel="noopener noreferrer">
                Newsletter
              </a>
              <a href="https://lounge.land/all-access" target="_blank" rel="noopener noreferrer">
                All-Access
              </a>
              <a href="https://www.facebook.com/MouseWait/" target="_blank" rel="noopener noreferrer">
                Facebook
              </a>
              <a href="https://www.instagram.com/mousewait/" target="_blank" rel="noopener noreferrer">
                Instagram
              </a>
              <a href="https://twitter.com/mousewait" target="_blank" rel="noopener noreferrer">
                Twitter
              </a>
            </div>
            <div className={styles.fineprint}>
              © {currentYear} MouseWait for Disneyland &amp; Disney World. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Home;
